import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import { useLocation, useNavigate } from "react-router-dom"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
} from "@hugeicons/core-free-icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useWorkspace } from "@/workspaces/WorkspaceContext"

type TourStep = {
  id: string
  path: string
  target: string
  title: string
  body: string
  preview?: "task-filters" | "task-view"
  placement?: "top" | "bottom" | "left" | "right"
}

type TargetRect = {
  top: number
  left: number
  width: number
  height: number
}

type TourOverlayGeometry = {
  top: number
  left: number
  width: number
  height: number
}

type ProductTourContextValue = {
  startTour: () => void
  closeTour: () => void
  isOpen: boolean
}

const ProductTourContext = createContext<ProductTourContextValue | null>(null)

const TOUR_VERSION = "v1"

const TOUR_STEPS: TourStep[] = [
  {
    id: "home",
    path: "/home",
    target: "nav-home",
    title: "Главная",
    body: "Это база: здесь видно, что требует внимания, ваши проекты и последние изменения в рабочем пространстве.",
    placement: "right",
  },
  {
    id: "inbox",
    path: "/inbox",
    target: "nav-inbox",
    title: "Входящие",
    body: "Сюда попадают уведомления и события, которые лучше не потерять в ежедневной работе.",
    placement: "right",
  },
  {
    id: "tasks-filter",
    path: "/tasks",
    target: "tasks-filter",
    title: "Фильтры задач",
    body: "Здесь можно быстро найти задачи, отфильтровать их по статусу, проекту, исполнителю и другим полям.",
    preview: "task-filters",
    placement: "left",
  },
  {
    id: "tasks-view",
    path: "/tasks",
    target: "tasks-view",
    title: "Вид задач",
    body: "Переключайте задачи между списком, доской и таймлайном. В этом же меню настраиваются группировка, сортировка и видимые свойства.",
    preview: "task-view",
    placement: "left",
  },
  {
    id: "projects",
    path: "/projects",
    target: "nav-projects",
    title: "Проекты",
    body: "Проекты собирают задачи, сроки, участников и статусы в одну рабочую структуру.",
    placement: "right",
  },
  {
    id: "files",
    path: "/files",
    target: "nav-files",
    title: "Файлы",
    body: "Файлы хранят документы, вложения и материалы по проектам. Вид можно переключать между списком и сеткой.",
    placement: "right",
  },
  {
    id: "reports",
    path: "/reports",
    target: "nav-reports",
    title: "Отчёты",
    body: "В отчётах можно собирать дашборды и следить за состоянием работы через метрики и графики.",
    placement: "right",
  },
]

function storageKey(workspaceSlug: string) {
  return `chrona:tour:workspace-intro:${TOUR_VERSION}:${workspaceSlug}`
}

function readCompleted(workspaceSlug: string) {
  if (typeof window === "undefined") return true
  return window.localStorage.getItem(storageKey(workspaceSlug)) === "done"
}

function markCompleted(workspaceSlug: string) {
  window.localStorage.setItem(storageKey(workspaceSlug), "done")
}

function targetSelector(target: string) {
  return `[data-tour="${target}"]`
}

function readTargetRect(target: string): TargetRect | null {
  const element = document.querySelector<HTMLElement>(targetSelector(target))
  if (!element) return null
  const rect = element.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return null
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function getPopoverPosition(rect: TargetRect, placement: TourStep["placement"]) {
  const gap = 12
  const width = Math.min(340, window.innerWidth - 32)
  let top = rect.top + rect.height + gap
  let left = rect.left

  if (placement === "top") top = rect.top - 180 - gap
  if (placement === "left") {
    top = rect.top
    left = rect.left - width - gap
  }
  if (placement === "right") {
    top = rect.top
    left = rect.left + rect.width + gap
  }

  return {
    width,
    top: clamp(top, 16, Math.max(16, window.innerHeight - 220)),
    left: clamp(left, 16, Math.max(16, window.innerWidth - width - 16)),
  }
}

function getOverlayGeometry(rect: TargetRect): TourOverlayGeometry {
  const pad = 6
  return {
    top: Math.max(8, rect.top - pad),
    left: Math.max(8, rect.left - pad),
    width: Math.min(window.innerWidth - 16, rect.width + pad * 2),
    height: Math.min(window.innerHeight - 16, rect.height + pad * 2),
  }
}

export function ProductTourProvider({ children }: { children: ReactNode }) {
  const { workspace, path } = useWorkspace()
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null)
  const step = TOUR_STEPS[stepIndex]

  const closeTour = useCallback(() => {
    markCompleted(workspace.slug)
    setIsOpen(false)
  }, [workspace.slug])

  const startTour = useCallback(() => {
    setStepIndex(0)
    setIsOpen(true)
  }, [])

  useEffect(() => {
    if (!readCompleted(workspace.slug)) {
      const timer = window.setTimeout(() => setIsOpen(true), 700)
      return () => window.clearTimeout(timer)
    }
  }, [workspace.slug])

  useEffect(() => {
    if (!isOpen || !step) return
    const desiredPath = path(step.path)
    if (location.pathname !== desiredPath) {
      navigate(desiredPath)
    }
  }, [isOpen, location.pathname, navigate, path, step])

  useEffect(() => {
    if (!isOpen || !step) {
      setTargetRect(null)
      return
    }

    function updateRect() {
      const rect = readTargetRect(step.target)
      if (rect) setTargetRect(rect)
      return rect
    }

    let observer: MutationObserver | null = null

    if (!updateRect()) {
      observer = new MutationObserver(() => {
        if (updateRect()) observer?.disconnect()
      })
      observer.observe(document.body, { childList: true, subtree: true })
    }

    window.addEventListener("resize", updateRect)
    window.addEventListener("scroll", updateRect, true)

    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", updateRect)
      window.removeEventListener("scroll", updateRect, true)
    }
  }, [isOpen, location.pathname, step])

  const value = useMemo(
    () => ({ startTour, closeTour, isOpen }),
    [closeTour, isOpen, startTour],
  )

  function goNext() {
    if (stepIndex >= TOUR_STEPS.length - 1) {
      closeTour()
      return
    }
    setStepIndex((current) => current + 1)
  }

  function goBack() {
    setStepIndex((current) => Math.max(0, current - 1))
  }

  return (
    <ProductTourContext.Provider value={value}>
      {children}
      {isOpen && step
        ? createPortal(
            <ProductTourOverlay
              step={step}
              stepIndex={stepIndex}
              total={TOUR_STEPS.length}
              targetRect={targetRect}
              onBack={goBack}
              onNext={goNext}
              onClose={closeTour}
            />,
            document.body,
          )
        : null}
    </ProductTourContext.Provider>
  )
}

export function useProductTour() {
  const context = useContext(ProductTourContext)
  if (!context) {
    throw new Error("useProductTour must be used within ProductTourProvider")
  }
  return context
}

function ProductTourOverlay({
  step,
  stepIndex,
  total,
  targetRect,
  onBack,
  onNext,
  onClose,
}: {
  step: TourStep
  stepIndex: number
  total: number
  targetRect: TargetRect | null
  onBack: () => void
  onNext: () => void
  onClose: () => void
}) {
  const safeRect =
    targetRect ??
    ({
      top: window.innerHeight / 2 - 40,
      left: window.innerWidth / 2 - 120,
      width: 240,
      height: 80,
    } satisfies TargetRect)
  const popover = getPopoverPosition(safeRect, step.placement)
  const overlay = getOverlayGeometry(safeRect)
  const isLast = stepIndex === total - 1

  return (
    <div className="fixed inset-0 z-40 pointer-events-none">
      <TourScrim hole={overlay} />
      <div
        className="absolute rounded-lg border-2 border-primary shadow-[0_14px_36px_rgba(15,23,42,0.14)] transition-[transform,width,height] duration-300 ease-out animate-in fade-in-0 zoom-in-95"
        style={{
          top: 0,
          left: 0,
          width: overlay.width,
          height: overlay.height,
          transform: `translate3d(${overlay.left}px, ${overlay.top}px, 0)`,
        }}
      />
      <div
        key={step.id}
        className="pointer-events-auto absolute rounded-lg border bg-popover p-4 text-popover-foreground shadow-xl animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 duration-200"
        style={{
          top: popover.top,
          left: popover.left,
          width: popover.width,
        }}
      >
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-medium uppercase text-muted-foreground">
              Шаг {stepIndex + 1} из {total}
            </div>
            <h2 className="mt-1 text-sm font-semibold">{step.title}</h2>
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={onClose}
            aria-label="Закрыть тур"
          >
            <HugeiconsIcon icon={Cancel01Icon} strokeWidth={2} className="size-4" />
          </button>
        </div>
        <p className="text-sm leading-5 text-muted-foreground">{step.body}</p>
        <TourStepPreview preview={step.preview} />
        <div className="mt-4 flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn("h-8 gap-1.5 text-xs", stepIndex === 0 && "invisible")}
            onClick={onBack}
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} strokeWidth={2} className="size-3.5" />
            Назад
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={onClose}>
              Пропустить
            </Button>
            <Button type="button" size="sm" className="h-8 gap-1.5 text-xs" onClick={onNext}>
              {isLast ? "Готово" : "Далее"}
              {!isLast && (
                <HugeiconsIcon icon={ArrowRight01Icon} strokeWidth={2} className="size-3.5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function TourScrim({ hole }: { hole: TourOverlayGeometry }) {
  return (
    <svg className="absolute inset-0 size-full animate-in fade-in-0 duration-150">
      <path
        fill="rgba(15, 23, 42, 0.30)"
        fillRule="evenodd"
        d={[
          `M0 0H${window.innerWidth}V${window.innerHeight}H0Z`,
          `M${hole.left + 8} ${hole.top}`,
          `H${hole.left + hole.width - 8}`,
          `Q${hole.left + hole.width} ${hole.top} ${hole.left + hole.width} ${hole.top + 8}`,
          `V${hole.top + hole.height - 8}`,
          `Q${hole.left + hole.width} ${hole.top + hole.height} ${hole.left + hole.width - 8} ${hole.top + hole.height}`,
          `H${hole.left + 8}`,
          `Q${hole.left} ${hole.top + hole.height} ${hole.left} ${hole.top + hole.height - 8}`,
          `V${hole.top + 8}`,
          `Q${hole.left} ${hole.top} ${hole.left + 8} ${hole.top}`,
          "Z",
        ].join(" ")}
      />
    </svg>
  )
}

function TourStepPreview({ preview }: { preview?: TourStep["preview"] }) {
  if (preview === "task-filters") return <TaskFiltersPreview />
  if (preview === "task-view") return <TaskViewPreview />
  return null
}

function PreviewCheck({ active = false }: { active?: boolean }) {
  return (
    <span
      className={cn(
        "flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-muted-foreground/30",
      )}
    >
      {active ? <span className="size-1.5 rounded-full bg-primary-foreground" /> : null}
    </span>
  )
}

function TaskFiltersPreview() {
  return (
    <div className="mt-3 rounded-lg border bg-background p-2 shadow-sm">
      <div className="mb-2 flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground">
        <span className="size-3 rounded-full border border-muted-foreground/40" />
        Поиск по задачам
      </div>
      <div className="space-y-1">
        {[
          { label: "Статус", value: "В работе", active: true },
          { label: "Проект", value: "Любой проект", active: false },
          { label: "Исполнитель", value: "Я", active: true },
          { label: "Срок", value: "На этой неделе", active: false },
        ].map((item) => (
          <div
            key={item.label}
            className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-xs hover:bg-muted/60"
          >
            <span className="flex items-center gap-2 text-muted-foreground">
              <PreviewCheck active={item.active} />
              {item.label}
            </span>
            <span className="max-w-32 truncate font-medium">{item.value}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-end border-t pt-2">
        <span className="rounded-md bg-primary px-2 py-1 text-[11px] font-medium text-primary-foreground">
          Применить
        </span>
      </div>
    </div>
  )
}

function TaskViewPreview() {
  return (
    <div className="mt-3 rounded-lg border bg-background p-2 shadow-sm">
      <div className="mb-2 grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
        {["Список", "Доска", "Таймлайн"].map((label, index) => (
          <div
            key={label}
            className={cn(
              "rounded-md px-2 py-1.5 text-center text-[11px]",
              index === 0 ? "bg-background font-medium shadow-sm" : "text-muted-foreground",
            )}
          >
            {label}
          </div>
        ))}
      </div>
      <div className="space-y-1 border-t pt-2">
        {[
          ["Строки", "По статусу"],
          ["Порядок", "Обновлено"],
          ["Свойства", "Приоритет, срок"],
        ].map(([label, value]) => (
          <div key={label} className="flex items-center gap-3 px-2 py-1.5 text-xs">
            <span className="w-16 shrink-0 text-muted-foreground">{label}</span>
            <span className="rounded px-1.5 py-1 font-medium hover:bg-muted">{value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
